import { useEffect, useState } from 'react';

export const BOOTSTRAP_MIN_MS = 3000;
export const LOADER_FADE_MS = 700;

interface BootstrapLoaderState {
    /** Whether the loader overlay is still in the DOM. */
    loaderMounted: boolean;
    /** Whether the loader has started its fade-out transition. */
    loaderFading: boolean;
}

/**
 * Manages the bootstrap loader lifecycle.
 *
 * Enforces a minimum display time (BOOTSTRAP_MIN_MS) so the loader is never
 * dismissed too quickly, then signals a fade-out (loaderFading) before finally
 * unmounting the overlay after the CSS transition completes (LOADER_FADE_MS).
 *
 * @param isLoading - true while the initial required data fetch is in-flight.
 */
export function useBootstrapLoader(isLoading: boolean): BootstrapLoaderState {
    const [minTimeElapsed, setMinTimeElapsed] = useState(false);
    const [loaderMounted, setLoaderMounted] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setMinTimeElapsed(true);
        }, BOOTSTRAP_MIN_MS);
        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (!isLoading && minTimeElapsed) {
            const timer = setTimeout(() => {
                setLoaderMounted(false);
            }, LOADER_FADE_MS);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isLoading, minTimeElapsed]);

    return {
        loaderMounted,
        loaderFading: !isLoading && minTimeElapsed,
    };
}
